import type {
  DataGridProProps,
  GridApi,
  GridEventListener,
  GridRowId,
  GridRowOrderChangeParams,
} from '@mui/x-data-grid-pro';
import { GRID_ROOT_GROUP_ID, gridRowNodeSelector, gridRowTreeSelector } from '@mui/x-data-grid-pro';
import { toast } from '@tempo-io/sui-sonner';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useItemTypeLevelsQuery } from '@/common/queries/useItemTypeLevelsQuery';
import { useItemTypesQuery } from '@/common/queries/useItemTypesQuery';
import { isParentManagedByIntegration } from '@/common/utils/parentLock';
import type { ItemsTableRow } from '../types';
import { type DragLegendContent, DragLegendPortal } from './DragLegendPortal';
import { calculateDropPosition, type DropTarget, isNoOpDrop, isSameParent } from './dropPosition';
import {
  isLevelCompatible,
  isSelectionLevelCompatible,
  validateParentReparent,
  validateSiblingReorder,
} from './rowReorderValidation';

interface OldRowState {
  parentId: GridRowId;
  index: number;
}

interface DragState {
  sourceId: GridRowId;
  dropTarget: DropTarget | null;
  oldState: OldRowState;
  prevSiblingId: GridRowId | null;
  nextSiblingId: GridRowId | null;
  expandedRowIds: Set<GridRowId>;
  selectionBlocked: boolean;
  selectionSpansParents: boolean;
  movableIds: GridRowId[];
  lockedCount: number;
  allLocked: boolean;
}

const INDICATOR_HEIGHT_PX = 2;

const positionIndicator = (
  indicator: HTMLDivElement,
  rowElement: HTMLElement,
  position: 'above' | 'below',
  mainEl: HTMLElement,
): void => {
  const rowRect = rowElement.getBoundingClientRect();
  const mainRect = mainEl.getBoundingClientRect();
  const rawTop =
    position === 'above' ? rowRect.top - mainRect.top - 1 : rowRect.bottom - mainRect.top - 1;
  const top = Math.max(0, Math.min(rawTop, mainRect.height - INDICATOR_HEIGHT_PX));
  indicator.style.top = `${top}px`;
  indicator.style.display = 'block';
};

const hideIndicator = (indicator: HTMLDivElement): void => {
  indicator.style.display = 'none';
};

const getSourceSelectionInfo = (
  api: GridApi,
  sourceId: GridRowId,
): { selectedRows: ReturnType<GridApi['getSelectedRows']>; isSourceSelected: boolean } => {
  const selectedRows = api.getSelectedRows();
  const sourceNum = Number(sourceId);
  const isSourceSelected = selectedRows.has(sourceNum) || selectedRows.has(sourceId);
  return { selectedRows, isSourceSelected };
};

const isRowReparentLocked = (api: GridApi, rowId: GridRowId): boolean => {
  const row = api.getRow(rowId) as ItemsTableRow | null;
  return row != null && isParentManagedByIntegration(row);
};

const buildRowOrderChangeParams = (
  sourceRowId: GridRowId,
  oldState: OldRowState,
  apiRef: React.RefObject<GridApi>,
): GridRowOrderChangeParams | null => {
  const api = apiRef.current;
  if (!api) return null;

  const row = api.getRow(sourceRowId);
  if (!row) return null;

  const parentNode = gridRowTreeSelector(apiRef)[oldState.parentId];
  if (!parentNode || !('children' in parentNode)) return null;

  const parent = oldState.parentId === GRID_ROOT_GROUP_ID ? null : oldState.parentId;

  return {
    row,
    oldIndex: oldState.index,
    targetIndex: parentNode.children.indexOf(sourceRowId),
    oldParent: parent,
    newParent: parent,
  };
};

const REPARENT_TARGET_CLASS = 'items-table-row-reparent-target';

export const useCustomRowReorder = (
  apiRef: React.RefObject<GridApi>,
  enabled: boolean,
  onRowOrderChange?: DataGridProProps['onRowOrderChange'],
  onReparent?: (
    itemIds: number[],
    newParentId: number | null,
    relativePriority?: { id: number; type: 'before' | 'after' },
  ) => Promise<void>,
  markExpanded?: (id: number) => void,
  siblingsOnly = false,
  paused = false,
): Pick<
  DataGridProProps<ItemsTableRow>,
  'isRowReorderable' | 'isValidRowReorder' | 'setTreeDataPath'
> & { dragLegend: React.ReactNode } => {
  const onRowOrderChangeRef = React.useRef(onRowOrderChange);
  onRowOrderChangeRef.current = onRowOrderChange;

  const onReparentRef = React.useRef(onReparent);
  onReparentRef.current = onReparent;

  const markExpandedRef = React.useRef(markExpanded);
  markExpandedRef.current = markExpanded;

  const siblingsOnlyRef = React.useRef(siblingsOnly);
  siblingsOnlyRef.current = siblingsOnly;

  const pausedRef = React.useRef(paused);
  pausedRef.current = paused;

  const { t } = useTranslation();
  const tRef = React.useRef(t);
  tRef.current = t;

  const { data: itemTypes } = useItemTypesQuery({ enabled });
  const { data: itemTypeLevels } = useItemTypeLevelsQuery({ enabled });

  const levelByTypeId = React.useMemo(() => {
    if (!itemTypes || !itemTypeLevels) return null;
    const levelById = new Map(itemTypeLevels.map((l) => [l.id, l.level]));
    return new Map(itemTypes.map((type) => [type.id, levelById.get(type.itemLevelId)]));
  }, [itemTypes, itemTypeLevels]);

  const levelByTypeIdRef = React.useRef(levelByTypeId);
  levelByTypeIdRef.current = levelByTypeId;

  const itemTypesRef = React.useRef(itemTypes);
  itemTypesRef.current = itemTypes;

  // Level names are org-configurable and one-per-tier, unlike item type names (which can be
  // many-per-tier) — using the level's own name avoids an unbounded "X or Y or Z or..." hint.
  const levelNameByLevel = React.useMemo(() => {
    if (!itemTypeLevels) return null;
    return new Map(itemTypeLevels.map((l) => [l.level, l.name]));
  }, [itemTypeLevels]);

  const levelNameByLevelRef = React.useRef(levelNameByLevel);
  levelNameByLevelRef.current = levelNameByLevel;

  const dragStateRef = React.useRef<DragState | null>(null);

  // Bundled together (rather than reading a ref for containerEl at render time) so a
  // single render always sees a consistent content/container pair — reading a mutable
  // ref's .current directly during render isn't safe to rely on in React.
  const [legendState, setLegendState] = React.useState<{
    content: DragLegendContent;
    containerEl: HTMLElement;
  } | null>(null);

  React.useEffect(() => {
    if (!enabled || !apiRef.current) return;
    const api = apiRef.current;
    const root = api.rootElementRef?.current;
    if (!root) return;

    const mainEl = root.querySelector<HTMLElement>('.MuiDataGrid-main') ?? root;
    const indicator = document.createElement('div');
    // Use inline styles so the indicator renders correctly regardless of Tailwind's
    // static scanning (dynamically created elements can be missed by the JIT scanner).
    indicator.style.cssText =
      'position:absolute;left:0;right:0;height:2px;background-color:var(--color-bg-accent-blue);pointer-events:none;z-index:31;display:none';
    mainEl.appendChild(indicator);

    const invalidIndicator = document.createElement('div');
    invalidIndicator.style.cssText =
      'position:absolute;left:0;right:0;height:2px;background-color:var(--color-border-error);pointer-events:none;z-index:31;display:none';
    mainEl.appendChild(invalidIndicator);

    const resolveSourceLevelName = (id: GridRowId): string | undefined => {
      const sourceRow = api.getRow(id) as ItemsTableRow | null;
      const type = itemTypesRef.current?.find((t) => t.id === sourceRow?.itemTypeId);
      const level = type ? levelByTypeIdRef.current?.get(type.id) : undefined;
      return level !== undefined ? levelNameByLevelRef.current?.get(level) : undefined;
    };

    // Computes the "where can this item move" hint entirely from itemTypes/itemTypeLevels,
    // already loaded client-side — no backend endpoint needed. Returns null to hide the hint segment.
    const computeDragHint = (
      sourceId: GridRowId,
      selectionCount: number,
      selectionBlocked: boolean,
      selectionSpansParents: boolean,
      lockedCount: number,
      allLocked: boolean,
      hoveredParentLevel?: number,
    ): string | null => {
      if (selectionBlocked) {
        return tRef.current(($) => $.views.table.dragBlockedMixedLevels);
      }

      const canReparent = !siblingsOnlyRef.current && Boolean(onReparentRef.current);

      if (canReparent && allLocked) {
        if (selectionSpansParents) {
          return tRef.current(($) => $.views.table.dragLegend.hintLockedMixedParents);
        }

        const lockedSourceLevelName = resolveSourceLevelName(sourceId);

        if (!lockedSourceLevelName) {
          return tRef.current(($) => $.views.table.reparentBlockedManagedByIntegration);
        }

        return tRef.current(
          ($) => $.views.table.dragLegend.hintReorderAmongLevelLockedByIntegration,
          {
            levelName: lockedSourceLevelName,
            count: selectionCount,
          },
        );
      }

      if (canReparent && lockedCount > 0) {
        return tRef.current(($) => $.views.table.dragLegend.hintSomeLockedByIntegration, {
          count: lockedCount,
        });
      }

      const row = api.getRow(sourceId) as ItemsTableRow | null;
      const sourceType = itemTypesRef.current?.find((type) => type.id === row?.itemTypeId);
      const sourceLevel = sourceType ? levelByTypeIdRef.current?.get(sourceType.id) : undefined;

      if (!sourceType?.name || sourceLevel === undefined) return null;

      // Planner (siblingsOnly): reparenting is disabled by table mode, regardless of hierarchy.
      if (siblingsOnlyRef.current) {
        return tRef.current(($) => $.views.table.dragLegend.hintSiblingsOnly, {
          itemType: sourceType.name,
        });
      }

      // isSameParent/validateSiblingReorder only check tree-parent equality, never type/level —
      // and every unparented item shares the same pseudo-parent (GRID_ROOT_GROUP_ID), regardless
      // of type or configured level. So an unparented item can *always* be reordered against ANY
      // other unparented item, independent of whether a valid parent type also exists for it — this
      // is a "no parent" fact, not a hierarchy-position fact ("top-level" would be wrong: a level-5
      // item with no parent right now is in exactly this situation).
      const node = gridRowNodeSelector(apiRef, sourceId);
      const isUnparented = node?.parent === GRID_ROOT_GROUP_ID;

      // The level's own name (not the item type's) — a level can have arbitrarily many types
      // sharing it, but exactly one configured name, and isLevelCompatible's cross-parent
      // reorder fallback is scoped by level, not by type.
      const sourceLevelName = resolveSourceLevelName(sourceId);
      const targetLevel = hoveredParentLevel ?? sourceLevel - 1;
      const hasValidParentType =
        hoveredParentLevel !== undefined ||
        (itemTypesRef.current ?? []).some(
          (type) => levelByTypeIdRef.current?.get(type.id) === targetLevel,
        );

      // No type sits one tier above this item, so reparenting is impossible — only
      // same-level reordering remains (isValidRowReorder's cross-parent fallback: any other
      // row at this level, regardless of tree parent).
      if (!hasValidParentType) {
        if (isUnparented) {
          return tRef.current(($) => $.views.table.dragLegend.hintReorderUnparented);
        }
        if (!sourceLevelName) return null;
        return tRef.current(($) => $.views.table.dragLegend.hintReorderAmongLevel, {
          levelName: sourceLevelName,
        });
      }

      const targetLevelName = levelNameByLevelRef.current?.get(targetLevel);
      if (!targetLevelName) return null;

      // Both facts hold at once when the item is currently unparented: it can drop into a valid
      // parent, AND it can freely reorder against any other unparented item regardless of level.
      // Showing only "Drop before/after" would hide the second, non-obvious capability.
      if (isUnparented) {
        return tRef.current(($) => $.views.table.dragLegend.hintReorderUnparentedOrDropInside, {
          levelName: targetLevelName,
        });
      }

      // A real (non-root) parent doesn't rule out the cross-parent same-level reorder fallback —
      // isValidRowReorder allows it independent of reparenting eligibility. Hiding it would make
      // the hint look like reparenting is the only option.
      if (!sourceLevelName) {
        return tRef.current(($) => $.views.table.dragLegend.hintDropInside, {
          levelName: targetLevelName,
        });
      }
      return tRef.current(($) => $.views.table.dragLegend.hintDropInsideOrReorderAmongLevel, {
        targetLevelName,
        sourceLevelName,
      });
    };

    const showDragLegend = (
      sourceId: GridRowId,
      selectionCount: number,
      selectionBlocked: boolean,
      selectionSpansParents: boolean,
      lockedCount: number,
      allLocked: boolean,
      hoveredParentLevel?: number,
    ): void => {
      const row = api.getRow(sourceId) as ItemsTableRow | null;
      const itemType = itemTypesRef.current?.find((type) => type.id === row?.itemTypeId);

      setLegendState({
        containerEl: mainEl,
        content: {
          color: itemType?.color,
          typeLabel: selectionCount > 1 ? null : (itemType?.name ?? null),
          primaryLabel:
            selectionCount > 1
              ? tRef.current(($) => $.views.table.dragLegend.itemsSelected, {
                  count: selectionCount,
                })
              : (row?.name ?? String(sourceId)),
          hint: computeDragHint(
            sourceId,
            selectionCount,
            selectionBlocked,
            selectionSpansParents,
            lockedCount,
            allLocked,
            hoveredParentLevel,
          ),
        },
      });
    };

    const getTypeLevelOrder = (rowId: GridRowId): number | undefined => {
      const map = levelByTypeIdRef.current;
      if (!map) return undefined;
      const row = api.getRow(rowId) as ItemsTableRow | null;
      const itemTypeId = row?.itemTypeId;
      return itemTypeId !== undefined ? map.get(itemTypeId) : undefined;
    };

    const getDraggedIds = (sourceId: GridRowId): GridRowId[] => {
      const { selectedRows, isSourceSelected } = getSourceSelectionInfo(api, sourceId);
      if (!isSourceSelected) return [sourceId];
      const sourceNum = Number(sourceId);
      const others = Array.from(selectedRows.keys()).filter((id) => Number(id) !== sourceNum);
      return [sourceId, ...others];
    };

    const getParentIdsOf = (ids: GridRowId[]): (GridRowId | null)[] =>
      ids.map((id) => gridRowNodeSelector(apiRef, id)?.parent ?? null);

    const partitionByLock = (
      sourceId: GridRowId,
    ): { movableIds: GridRowId[]; lockedCount: number; allLocked: boolean } => {
      const ids = getDraggedIds(sourceId);
      const movableIds = ids.filter((id) => !isRowReparentLocked(api, id));
      return {
        movableIds,
        lockedCount: ids.length - movableIds.length,
        allLocked: movableIds.length === 0,
      };
    };

    const isSelectionMixedLevel = (sourceId: GridRowId): boolean => {
      const { isSourceSelected } = getSourceSelectionInfo(api, sourceId);
      if (!isSourceSelected) return false;
      const ids = getDraggedIds(sourceId);
      if (ids.length <= 1) return false;
      const members = ids.flatMap((id) => {
        const node = gridRowNodeSelector(apiRef, id);
        if (!node) return [];
        return [
          {
            level: getTypeLevelOrder(id),
            isUnparented: node.parent === GRID_ROOT_GROUP_ID,
            depth: node.depth,
          },
        ];
      });
      return !isSelectionLevelCompatible(members);
    };

    const countDistinctParents = (sourceId: GridRowId): number =>
      new Set(getDraggedIds(sourceId).map((id) => gridRowNodeSelector(apiRef, id)?.parent)).size;

    const unsubStart = api.subscribeEvent('rowDragStart', (params) => {
      if (pausedRef.current) return;
      const node = gridRowNodeSelector(apiRef, params.id);
      if (node && node.parent !== null) {
        const parentNode = gridRowNodeSelector(apiRef, node.parent);
        const parentChildren = parentNode && 'children' in parentNode ? parentNode.children : [];
        const index = parentChildren.indexOf(params.id);

        const tree = gridRowTreeSelector(apiRef);
        const expandedRowIds = new Set(
          Object.entries(tree)
            .filter(
              ([, n]) => 'children' in n && (n as { childrenExpanded: boolean }).childrenExpanded,
            )
            .map(([id]) => id as GridRowId),
        );

        dragStateRef.current = {
          sourceId: params.id,
          dropTarget: null,
          oldState: { parentId: node.parent, index },
          prevSiblingId: parentChildren[index - 1] ?? null,
          nextSiblingId: parentChildren[index + 1] ?? null,
          expandedRowIds,
          selectionBlocked: isSelectionMixedLevel(params.id),
          selectionSpansParents: countDistinctParents(params.id) > 1,
          ...partitionByLock(params.id),
        };
        reparentBlockedTarget = false;

        const { selectedRows, isSourceSelected } = getSourceSelectionInfo(api, params.id);
        showDragLegend(
          params.id,
          isSourceSelected ? selectedRows.size : 1,
          dragStateRef.current.selectionBlocked,
          dragStateRef.current.selectionSpansParents,
          dragStateRef.current.lockedCount,
          dragStateRef.current.allLocked,
        );
      }
    });

    let pendingRaf: number | null = null;
    let latestDragOverParams: { id: GridRowId } | null = null;
    let latestDragOverEvent: Parameters<GridEventListener<'rowDragOver'>>[1] | null = null;
    let rowElCache: { id: GridRowId; el: HTMLElement } | null = null;
    let reparentHighlightEl: HTMLElement | null = null;
    let reparentBlockedTarget = false;

    const clearReparentHighlight = (): void => {
      reparentHighlightEl?.classList.remove(REPARENT_TARGET_CLASS);
      reparentHighlightEl = null;
    };

    const handleDragOver: GridEventListener<'rowDragOver' | 'cellDragOver'> = (
      params,
      event,
    ): void => {
      latestDragOverParams = params;
      latestDragOverEvent = event;

      if (pendingRaf !== null) return;

      pendingRaf = requestAnimationFrame(() => {
        pendingRaf = null;

        const currentParams = latestDragOverParams;
        const currentEvent = latestDragOverEvent;
        if (!currentParams || !currentEvent) return;

        const dragState = dragStateRef.current;
        if (!dragState) return;

        // Re-derived every pass so the flag always reflects the last hovered
        // target: hovering a blocked parent then dropping back on a sibling
        // must not toast.
        reparentBlockedTarget = false;

        const { sourceId, prevSiblingId, nextSiblingId } = dragState;
        const targetId = currentParams.id;

        const rowEl = rowElCache?.id === targetId ? rowElCache.el : api.getRowElement(targetId);
        if (rowEl && rowElCache?.id !== targetId) {
          rowElCache = { id: targetId, el: rowEl };
        }

        if (!rowEl) {
          hideIndicator(indicator);
          hideIndicator(invalidIndicator);
          clearReparentHighlight();
          dragStateRef.current = { ...dragState, dropTarget: null };
          return;
        }

        const sourceNode = gridRowNodeSelector(apiRef, sourceId);
        const targetNode = gridRowNodeSelector(apiRef, targetId);
        if (!sourceNode || !targetNode) {
          hideIndicator(indicator);
          hideIndicator(invalidIndicator);
          clearReparentHighlight();
          dragStateRef.current = { ...dragState, dropTarget: null };
          return;
        }

        const rejectDrop = (): void => {
          hideIndicator(indicator);
          clearReparentHighlight();
          dragStateRef.current = { ...dragState, dropTarget: null };
          if (currentEvent.dataTransfer) currentEvent.dataTransfer.dropEffect = 'none';
          const position = calculateDropPosition(
            currentEvent.clientY,
            rowEl.getBoundingClientRect(),
          );
          positionIndicator(invalidIndicator, rowEl, position, mainEl);
        };

        if (dragState.selectionBlocked) {
          rejectDrop();
          return;
        }

        hideIndicator(invalidIndicator);

        const sourceLevel = getTypeLevelOrder(sourceId);
        const targetLevel = getTypeLevelOrder(targetId);

        const wouldReparent =
          !siblingsOnlyRef.current &&
          Boolean(onReparentRef.current) &&
          validateParentReparent({
            sourceNode,
            targetNode,
            sourceTypeLevelOrder: sourceLevel,
            targetTypeLevelOrder: targetLevel,
            selectionParentIds: getParentIdsOf(dragState.movableIds),
          });

        if (wouldReparent && dragState.allLocked) {
          reparentBlockedTarget = true;
          rejectDrop();
          return;
        }

        const moveSelectionAsCrossParent =
          dragState.selectionSpansParents &&
          !siblingsOnlyRef.current &&
          Boolean(onReparentRef.current);

        if (wouldReparent) {
          hideIndicator(indicator);
          if (reparentHighlightEl !== rowEl) {
            clearReparentHighlight();
            rowEl.classList.add(REPARENT_TARGET_CLASS);
            reparentHighlightEl = rowEl;
            showDragLegend(
              sourceId,
              dragState.movableIds.length + dragState.lockedCount,
              dragState.selectionBlocked,
              dragState.selectionSpansParents,
              dragState.lockedCount,
              dragState.allLocked,
              targetLevel,
            );
          }
          dragStateRef.current = {
            ...dragState,
            dropTarget: { rowId: targetId, position: 'inside' },
          };
        } else if (isSameParent(sourceNode, targetNode) && !moveSelectionAsCrossParent) {
          clearReparentHighlight();
          const position = calculateDropPosition(
            currentEvent.clientY,
            rowEl.getBoundingClientRect(),
          );

          if (isNoOpDrop(sourceId, targetId, position, prevSiblingId, nextSiblingId)) {
            hideIndicator(indicator);
            dragStateRef.current = { ...dragState, dropTarget: null };
            return;
          }

          dragStateRef.current = { ...dragState, dropTarget: { rowId: targetId, position } };
          positionIndicator(indicator, rowEl, position, mainEl);
        } else if (siblingsOnlyRef.current) {
          rejectDrop();
          return;
        } else {
          // Case B: cross-parent same-level drop — source moves to target's parent.
          if (targetId === sourceId) {
            hideIndicator(indicator);
            clearReparentHighlight();
            dragStateRef.current = { ...dragState, dropTarget: null };
            return;
          }

          if (dragState.allLocked) {
            reparentBlockedTarget = true;
            rejectDrop();
            return;
          }

          if (!isLevelCompatible(sourceLevel, targetLevel, sourceNode.depth, targetNode.depth)) {
            rejectDrop();
            return;
          }

          clearReparentHighlight();
          const position = calculateDropPosition(
            currentEvent.clientY,
            rowEl.getBoundingClientRect(),
          );
          dragStateRef.current = {
            ...dragState,
            dropTarget: { rowId: targetId, position, isCrossParent: true },
          };
          positionIndicator(indicator, rowEl, position, mainEl);
        }

        if (currentEvent.dataTransfer) {
          currentEvent.dataTransfer.dropEffect = 'move';
        }
      });
    };

    const handleDragLeave = (e: DragEvent): void => {
      if (mainEl.contains(e.relatedTarget as Node)) return;
      hideIndicator(indicator);
      hideIndicator(invalidIndicator);
      clearReparentHighlight();
      reparentBlockedTarget = false;
    };

    const unsubOver = api.subscribeEvent('rowDragOver', handleDragOver);
    const unsubCellOver = api.subscribeEvent('cellDragOver', handleDragOver);
    mainEl.addEventListener('dragleave', handleDragLeave);

    const unsubExpansion = api.subscribeEvent('rowExpansionChange', (params) => {
      if (
        dragStateRef.current !== null &&
        params.childrenExpanded &&
        !dragStateRef.current.expandedRowIds.has(params.id)
      ) {
        api.setRowChildrenExpansion(params.id, false);
      }
    });

    const unsubEnd = api.subscribeEvent('rowDragEnd', (_params, event) => {
      hideIndicator(indicator);
      hideIndicator(invalidIndicator);
      clearReparentHighlight();
      setLegendState(null);

      if (pendingRaf !== null) {
        cancelAnimationFrame(pendingRaf);
        pendingRaf = null;
      }
      latestDragOverParams = null;
      latestDragOverEvent = null;
      rowElCache = null;

      const blockedByLock = reparentBlockedTarget;
      reparentBlockedTarget = false;

      const dragState = dragStateRef.current;
      dragStateRef.current = null;

      const { clientX, clientY } = event;
      const mainRect = mainEl.getBoundingClientRect();
      const droppedOutside =
        (clientX === 0 && clientY === 0) ||
        clientX < mainRect.left ||
        clientX > mainRect.right ||
        clientY < mainRect.top ||
        clientY > mainRect.bottom;

      if (dragState?.selectionBlocked) {
        if (!droppedOutside) {
          toast.error(tRef.current(($) => $.views.table.dragBlockedMixedLevels));
        }
        return;
      }

      if (blockedByLock) {
        return;
      }

      if (!dragState?.dropTarget) return;
      if (droppedOutside) return;

      const { sourceId, dropTarget, oldState } = dragState;

      const sourceNode = gridRowNodeSelector(apiRef, sourceId);
      const targetNode = gridRowNodeSelector(apiRef, dropTarget.rowId);
      if (!sourceNode || !targetNode) return;

      const reparentIds = dragState.movableIds.map(Number).filter((id) => Number.isFinite(id));

      const notifySkippedByIntegration = (): void => {
        if (dragState.lockedCount === 0) return;
        toast.info(
          tRef.current(($) => $.common.bulkEdit.parentSkippedImported, {
            count: dragState.lockedCount,
          }),
        );
      };

      const toRelativePriority = (
        rowId: GridRowId,
        position: 'above' | 'below',
      ): { id: number; type: 'before' | 'after' } =>
        position === 'above'
          ? { id: Number(rowId), type: 'before' }
          : { id: Number(rowId), type: 'after' };

      if (dropTarget.isCrossParent) {
        const sourceLevel = getTypeLevelOrder(sourceId);
        const targetLevel = getTypeLevelOrder(dropTarget.rowId);
        if (!isLevelCompatible(sourceLevel, targetLevel, sourceNode.depth, targetNode.depth)) {
          return;
        }
        if (reparentIds.length === 0) return;

        const newParentId =
          targetNode.parent === GRID_ROOT_GROUP_ID ? null : Number(targetNode.parent);
        const relPriority =
          dropTarget.position !== 'inside'
            ? toRelativePriority(dropTarget.rowId, dropTarget.position)
            : undefined;
        void onReparentRef
          .current?.(reparentIds, newParentId, relPriority)
          ?.then(notifySkippedByIntegration)
          ?.catch(() => {});
        return;
      }

      if (dropTarget.position === 'inside') {
        if (
          !validateParentReparent({
            sourceNode,
            targetNode,
            sourceTypeLevelOrder: getTypeLevelOrder(sourceId),
            targetTypeLevelOrder: getTypeLevelOrder(dropTarget.rowId),
            selectionParentIds: getParentIdsOf(dragState.movableIds),
          })
        )
          return;
        if (reparentIds.length === 0) return;
        const parentId = Number(dropTarget.rowId);
        markExpandedRef.current?.(parentId);
        void onReparentRef
          .current?.(reparentIds, parentId)
          ?.then(() => {
            notifySkippedByIntegration();
            apiRef.current?.setRowChildrenExpansion(parentId, true);
          })
          ?.catch(() => {});
        return;
      }

      const prevNode = dragState.prevSiblingId
        ? (gridRowNodeSelector(apiRef, dragState.prevSiblingId) ?? null)
        : null;
      const nextNode = dragState.nextSiblingId
        ? (gridRowNodeSelector(apiRef, dragState.nextSiblingId) ?? null)
        : null;

      const isValid = validateSiblingReorder({
        sourceNode,
        targetNode,
        prevNode,
        nextNode,
        dropPosition: dropTarget.position,
      });

      if (!isValid) return;

      try {
        api.setRowPosition(sourceId, dropTarget.rowId, dropTarget.position);
        const params = buildRowOrderChangeParams(sourceId, oldState, apiRef);
        if (params) {
          onRowOrderChangeRef.current?.(params, event, { api });
        }
      } catch {
        // Reorder failed — grid state is unchanged, nothing to roll back
      }
    });

    return () => {
      unsubStart();
      unsubExpansion();
      unsubOver();
      unsubCellOver();
      unsubEnd();
      mainEl.removeEventListener('dragleave', handleDragLeave);
      if (pendingRaf !== null) {
        cancelAnimationFrame(pendingRaf);
      }
      clearReparentHighlight();
      indicator.remove();
      invalidIndicator.remove();
      setLegendState(null);
    };
  }, [apiRef, enabled]);

  const isRowReorderable = React.useCallback<
    NonNullable<DataGridProProps<ItemsTableRow>['isRowReorderable']>
  >(() => !paused, [paused]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: apiRef and levelByTypeIdRef are stable refs; reading .current inside [] deps is the established pattern in this hook
  const isValidRowReorder = React.useCallback<
    NonNullable<DataGridProProps<ItemsTableRow>['isValidRowReorder']>
  >(({ sourceNode, targetNode, dropPosition }) => {
    // Case A (center zone reparent) is handled entirely by the custom subscription.
    if (dropPosition === 'inside') return false;
    // Same-parent reorder is handled by the custom subscription (optimistic setRowPosition
    // + manual onRowOrderChange call) to preserve the #8640 blue indicator behaviour.
    if (sourceNode.parent === targetNode.parent) return false;
    // Siblings-only mode (e.g. planner) forbids any cross-parent move.
    if (siblingsOnlyRef.current) return false;
    const api = apiRef.current;
    if (api && isRowReparentLocked(api, sourceNode.id)) return false;
    const sourceRow = api?.getRow(sourceNode.id) as ItemsTableRow | null;
    // Returning false prevents MUI's CrossParentLeafOperation from moving the item
    // optimistically — no tree update means no snap-back on incompatible drops.
    const targetRow = api?.getRow(targetNode.id) as ItemsTableRow | null;
    const levelMap = levelByTypeIdRef.current;
    const sl =
      sourceRow?.itemTypeId !== undefined ? levelMap?.get(sourceRow.itemTypeId) : undefined;
    const tl =
      targetRow?.itemTypeId !== undefined ? levelMap?.get(targetRow.itemTypeId) : undefined;
    return isLevelCompatible(sl, tl, sourceNode.depth, targetNode.depth);
  }, []);

  const setTreeDataPath = React.useCallback(
    (path: string[], row: ItemsTableRow): ItemsTableRow => ({
      ...row,
      parentId: path.length > 1 ? Number(path[path.length - 2]) : undefined,
    }),
    [],
  );

  return {
    isRowReorderable,
    isValidRowReorder,
    setTreeDataPath,
    dragLegend: (
      <DragLegendPortal
        content={legendState?.content ?? null}
        containerEl={legendState?.containerEl ?? null}
        validLabel={t(($) => $.views.table.dragLegend.valid)}
        invalidLabel={t(($) => $.views.table.dragLegend.invalid)}
      />
    ),
  };
};
